// src/components/progress/ProgressPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import {
  getIndiaDate,
  getIndiaDateString,
  getLekhiCompletionDates,
  getLekhiActivityForUser,
  getLekhiTestResultsForUser,
  getLekhiTestQuestionsByNumber
} from '../../services/firebase/firestoreService';
import './ProgressPage.css';
import '../lekhi/LekhiSundayTestPage.css';

const SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];

const SUBJECT_LABELS_MR = {
  grammar: 'मराठी',
  math: 'गणित',
  reasoning: 'बुद्धिमत्ता',
  gk: 'सामान्य ज्ञान'
};

const SUBJECT_LABELS_EN = {
  grammar: 'Marathi',
  math: 'Math',
  reasoning: 'Reasoning',
  gk: 'GK'
};

const LEVEL_LABELS_MR = {
  easy: 'प्रारंभ',
  medium: 'प्रगत',
  hard: 'प्रावीण्य'
};

const LEVEL_LABELS_EN = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard'
};

const TOPICS_PER_SUBJECT = 15;

const DropPin = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"
      fill="#007fff"
    />
  </svg>
);

const getWeekKeyForDate = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  const weekNumber = 1 + Math.ceil((firstThursday - target) / 604800000);
  return `${date.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
};

const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const dateToStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ProgressPage = () => {
  const { userData } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const [activeTab, setActiveTab] = useState('fitness');

  const [loading, setLoading] = useState(true);
  const [weeksData, setWeeksData] = useState([]);
  const [, setUserWeekNumber] = useState(1);
  const [totalFitnessDays, setTotalFitnessDays] = useState(0);

  const [lekhiLoading, setLekhiLoading] = useState(false);
  const [lekhiWeeksData, setLekhiWeeksData] = useState([]);
  const [totalLekhiDays, setTotalLekhiDays] = useState(0);
  const [topicsCoverage, setTopicsCoverage] = useState({});
  const [testResults, setTestResults] = useState([]);

  const [viewingTestList, setViewingTestList] = useState(false);
  const [viewingTestReview, setViewingTestReview] = useState(null);
  const [reviewSubjectIndex, setReviewSubjectIndex] = useState(0);

  const scrollRef1 = useRef(null);
  const scrollRef2 = useRef(null);
  const scrollRef3 = useRef(null);
  const lekhiScrollRef1 = useRef(null);
  const lekhiScrollRef2 = useRef(null);

  const t = language === 'mr' ? {
    title: 'प्रगती',
    weeks: 'आठवडे',
    trainingDays: 'प्रशिक्षण दिवस',
    squats: 'उठाबशा',
    jumpingJacks: 'जंपिंग जॅक्स',
    fitnessHero: (n) => `तू ${n} दिवस व्यायाम केला आहेस! 💪`,
    fitnessHeroZero: 'तू तुझा पहिला दिवस सुरू केला आहेस! 💪',
    lekhiHero: (n) => `तू ${n} दिवस लेखीचा अभ्यास केला आहेस! 📚`,
    lekhiHeroZero: 'तुझा लेखीचा प्रवास सुरू कर! 📚',
    lekhiStudyDays: 'अभ्यासाचे दिवस',
    topicsCovered: 'विषय पूर्ण केले',
    topicsLabel: (covered) => `${covered} / ${TOPICS_PER_SUBJECT} विषय`,
    sundayTests: 'रविवारच्या परीक्षा',
    testEmpty: 'तुमची पहिली परीक्षा रविवारी आहे',
    reviewPastTests: 'मागील परीक्षा पाहा',
    reviewListTitle: 'मागील परीक्षा',
    test: 'परीक्षा',
    backToProgress: '← मागे',
    reviewPrevBtn: 'मागील विषय',
    reviewNextBtn: 'पुढील विषय',
    reviewDoneBtn: 'मी पाहिले',
    yourAnswer: 'तुझे उत्तर',
    correctAnswer: 'योग्य उत्तर',
    emptyAnswer: 'रिकामे',
    explanation: 'स्पष्टीकरण',
    total: 'एकूण'
  } : {
    title: 'Progress',
    weeks: 'Weeks',
    trainingDays: 'Training Days',
    squats: 'Squats',
    jumpingJacks: 'Jumping Jacks',
    fitnessHero: (n) => `You've worked out for ${n} days! 💪`,
    fitnessHeroZero: "You've started your first day! 💪",
    lekhiHero: (n) => `You've studied Lekhi for ${n} days! 📚`,
    lekhiHeroZero: 'Start your Lekhi journey! 📚',
    lekhiStudyDays: 'Study Days',
    topicsCovered: 'Topics Covered',
    topicsLabel: (covered) => `${covered} / ${TOPICS_PER_SUBJECT} topics`,
    sundayTests: 'Sunday Tests',
    testEmpty: 'Your first test is this Sunday',
    reviewPastTests: 'Review Past Tests',
    reviewListTitle: 'Past Tests',
    test: 'Test',
    backToProgress: '← Back',
    reviewPrevBtn: 'Previous',
    reviewNextBtn: 'Next',
    reviewDoneBtn: 'Done',
    yourAnswer: 'Your answer',
    correctAnswer: 'Correct answer',
    emptyAnswer: 'Empty',
    explanation: 'Explanation',
    total: 'total'
  };

  const subjectLabel = (s) => language === 'mr' ? SUBJECT_LABELS_MR[s] : SUBJECT_LABELS_EN[s];
  const levelLabel = (l) => language === 'mr' ? LEVEL_LABELS_MR[l] : LEVEL_LABELS_EN[l];

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  useEffect(() => {
    const loadFitnessData = async () => {
      if (!userData?.id) return;
      try {
        const signupTimestamp = userData.fitnessStartDate || userData.createdAt;
        if (!signupTimestamp) {
          setLoading(false);
          return;
        }
        const signupDate = signupTimestamp.seconds
          ? new Date(signupTimestamp.seconds * 1000)
          : new Date(signupTimestamp);

        const statsSnap = await getDoc(doc(db, 'fitnessUserStats', userData.id));
        const weeklyStats = statsSnap.exists() ? (statsSnap.data().weeklyStats || {}) : {};

        const todayIndia = getIndiaDate();
        const signupMonday = getMondayOfWeek(signupDate);
        const todayMonday = getMondayOfWeek(todayIndia);
        const currentWeekIndex = Math.floor(
          (todayMonday - signupMonday) / (7 * 24 * 60 * 60 * 1000)
        );
        const totalWeeks = currentWeekIndex + 1;
        setUserWeekNumber(totalWeeks);

        const todayStr = getIndiaDateString();
        const signupStr = dateToStr(signupDate);

        const weekMetadata = [];

        for (let i = 0; i < totalWeeks; i++) {
          const weekMonday = new Date(signupMonday);
          weekMonday.setDate(signupMonday.getDate() + i * 7);

          const weekKey = getWeekKeyForDate(weekMonday);
          const weekStats = weeklyStats[weekKey] || { squats: 0, jumpingJacks: 0 };

          const days = [];
          for (let d = 0; d < 7; d++) {
            const dayDate = new Date(weekMonday);
            dayDate.setDate(weekMonday.getDate() + d);
            const dayStr = dateToStr(dayDate);
            const isBeforeSignup = dayStr < signupStr;
            const isFuture = dayStr > todayStr;
            days.push({
              dateStr: dayStr,
              isBeforeSignup,
              isFuture,
              isPast: !isBeforeSignup && !isFuture
            });
          }

          weekMetadata.push({
            userWeekNum: i + 1,
            days,
            squats: weekStats.squats || 0,
            jumpingJacks: weekStats.jumpingJacks || 0,
            isCurrentWeek: i === currentWeekIndex
          });
        }

        const relevantDays = [];
        weekMetadata.forEach(w => {
          w.days.forEach(d => {
            if (d.isPast) relevantDays.push(d.dateStr);
          });
        });

        const completionMap = {};
        if (relevantDays.length > 0) {
          const snaps = await Promise.all(
            relevantDays.map(dateStr =>
              getDoc(doc(db, 'fitnessDailyCompletions', `${userData.id}_${dateStr}`))
            )
          );
          snaps.forEach((snap, idx) => {
            const dateStr = relevantDays[idx];
            completionMap[dateStr] = snap.exists() && snap.data()?.allTasksCompleted === true;
          });
        }

        let totalDays = 0;
        weekMetadata.forEach(w => {
          w.days = w.days.map(d => {
            const isCompleted = d.isPast ? (completionMap[d.dateStr] || false) : false;
            if (isCompleted) totalDays++;
            return { ...d, isCompleted };
          });
        });
        setTotalFitnessDays(totalDays);

        setWeeksData(weekMetadata);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    };
    loadFitnessData();
  }, [userData]);

  useEffect(() => {
    const loadLekhiData = async () => {
      if (!userData?.id || activeTab !== 'lekhi') return;
      if (lekhiWeeksData.length > 0) return;
      setLekhiLoading(true);
      try {
        const lekhiStartTs = userData.lekhiStartDate;
        if (!lekhiStartTs) {
          setLekhiLoading(false);
          return;
        }
        const lekhiStartDate = lekhiStartTs.seconds
          ? new Date(lekhiStartTs.seconds * 1000)
          : new Date(lekhiStartTs);

        const [completionDates, activityDocs, testDocs] = await Promise.all([
          getLekhiCompletionDates(userData.id),
          getLekhiActivityForUser(userData.id),
          getLekhiTestResultsForUser(userData.id)
        ]);

        const completionSet = new Set(completionDates);
        setTotalLekhiDays(completionDates.length);

        const todayIndia = getIndiaDate();
        const startMonday = getMondayOfWeek(lekhiStartDate);
        const todayMonday = getMondayOfWeek(todayIndia);
        const currentWeekIndex = Math.floor(
          (todayMonday - startMonday) / (7 * 24 * 60 * 60 * 1000)
        );
        const totalWeeks = currentWeekIndex + 1;

        const todayStr = getIndiaDateString();
        const startStr = dateToStr(lekhiStartDate);

        const weekMetadata = [];
        for (let i = 0; i < totalWeeks; i++) {
          const weekMonday = new Date(startMonday);
          weekMonday.setDate(startMonday.getDate() + i * 7);

          const days = [];
          for (let d = 0; d < 7; d++) {
            const dayDate = new Date(weekMonday);
            dayDate.setDate(weekMonday.getDate() + d);
            const dayStr = dateToStr(dayDate);
            const isBeforeStart = dayStr < startStr;
            const isFuture = dayStr > todayStr;
            const isPast = !isBeforeStart && !isFuture;
            const isCompleted = isPast && completionSet.has(dayStr);
            days.push({
              dateStr: dayStr,
              isBeforeStart,
              isFuture,
              isPast,
              isCompleted
            });
          }

          weekMetadata.push({
            userWeekNum: i + 1,
            days,
            isCurrentWeek: i === currentWeekIndex
          });
        }
        setLekhiWeeksData(weekMetadata);

        const coverage = {};
        SUBJECTS.forEach(s => { coverage[s] = {}; });
        activityDocs.forEach(d => {
          const s = d.subjectCode;
          const t = d.topicCode;
          const l = d.level;
          if (!coverage[s]) coverage[s] = {};
          if (!coverage[s][l]) coverage[s][l] = new Set();
          coverage[s][l].add(t);
        });
        const coverageCounts = {};
        SUBJECTS.forEach(s => {
          coverageCounts[s] = {};
          ['easy', 'medium', 'hard'].forEach(l => {
            if (coverage[s][l]) {
              coverageCounts[s][l] = coverage[s][l].size;
            }
          });
        });
        setTopicsCoverage(coverageCounts);

        const sortedTests = testDocs.sort((a, b) => (a.testNumber || 0) - (b.testNumber || 0));
        setTestResults(sortedTests);

        setLekhiLoading(false);
      } catch (e) {
        setLekhiLoading(false);
      }
    };
    loadLekhiData();
  }, [userData, activeTab, lekhiWeeksData.length]);

  useEffect(() => {
    if (activeTab !== 'fitness' || loading || weeksData.length === 0) return;
    const scrollToCurrent = (ref) => {
      if (!ref.current) return;
      const container = ref.current;
      const currentIdx = weeksData.findIndex(w => w.isCurrentWeek);
      if (currentIdx === -1) return;
      const cols = container.querySelectorAll('.scrollable-col');
      if (cols[currentIdx]) {
        const colLeft = cols[currentIdx].offsetLeft;
        const colWidth = cols[currentIdx].offsetWidth;
        const containerWidth = container.offsetWidth;
        container.scrollLeft = colLeft - containerWidth + colWidth + 20;
      }
    };
    setTimeout(() => {
      scrollToCurrent(scrollRef1);
      scrollToCurrent(scrollRef2);
      scrollToCurrent(scrollRef3);
    }, 100);
  }, [activeTab, loading, weeksData]);

  useEffect(() => {
    if (activeTab !== 'lekhi' || lekhiLoading || lekhiWeeksData.length === 0) return;
    const scrollToCurrent = (ref) => {
      if (!ref.current) return;
      const container = ref.current;
      const currentIdx = lekhiWeeksData.findIndex(w => w.isCurrentWeek);
      if (currentIdx === -1) return;
      const cols = container.querySelectorAll('.scrollable-col');
      if (cols[currentIdx]) {
        const colLeft = cols[currentIdx].offsetLeft;
        const colWidth = cols[currentIdx].offsetWidth;
        const containerWidth = container.offsetWidth;
        container.scrollLeft = colLeft - containerWidth + colWidth + 20;
      }
    };
    setTimeout(() => {
      scrollToCurrent(lekhiScrollRef1);
      scrollToCurrent(lekhiScrollRef2);
    }, 100);
  }, [activeTab, lekhiLoading, lekhiWeeksData]);

  const totalSquats = weeksData.reduce((sum, w) => sum + (w.squats || 0), 0);
  const totalJJ = weeksData.reduce((sum, w) => sum + (w.jumpingJacks || 0), 0);
  const heroMessage = totalFitnessDays === 0 ? t.fitnessHeroZero : t.fitnessHero(totalFitnessDays);
  const lekhiHeroMessage = totalLekhiDays === 0 ? t.lekhiHeroZero : t.lekhiHero(totalLekhiDays);

  const maxSquats = Math.max(...weeksData.map(w => w.squats), 1);
  const maxJJ = Math.max(...weeksData.map(w => w.jumpingJacks), 1);
  const bestSquatsWeekIndex = weeksData.length > 0 ? weeksData.reduce((bestIdx, w, idx, arr) =>
    w.squats > arr[bestIdx].squats ? idx : bestIdx, 0) : -1;
  const bestJJWeekIndex = weeksData.length > 0 ? weeksData.reduce((bestIdx, w, idx, arr) =>
    w.jumpingJacks > arr[bestIdx].jumpingJacks ? idx : bestIdx, 0) : -1;

  const hasSquatsData = weeksData.some(w => w.squats > 0);
  const hasJJData = weeksData.some(w => w.jumpingJacks > 0);
  const todayStr = getIndiaDateString();

  const getDaysCompleted = (week) => week.days.filter(d => d.isCompleted).length;

  const handleReviewPastTests = () => {
    setViewingTestList(true);
  };

  const handlePickTest = async (testResult) => {
    try {
      const questions = await getLekhiTestQuestionsByNumber(testResult.testNumber);
      const userLevels = {
        grammar: userData.grammarLevel || 'easy',
        math: userData.mathLevel || 'easy',
        reasoning: userData.reasoningLevel || 'easy',
        gk: userData.gkLevel || 'easy'
      };
      const userQuestions = questions.filter(q => q.level === userLevels[q.subject]);
      const byTopic = {};
      userQuestions.forEach(q => {
        const key = `${q.subject}|${q.topic}`;
        if (!byTopic[key]) byTopic[key] = q;
      });
      const finalQs = Object.values(byTopic);

      const bySubject = { grammar: [], math: [], reasoning: [], gk: [] };
      finalQs.forEach(q => {
        if (bySubject[q.subject]) bySubject[q.subject].push(q);
      });

      const answers = {};
      (testResult.questionLevelData || []).forEach(qd => {
        if (qd.userAnswer) answers[qd.qId] = qd.userAnswer;
      });

      setViewingTestReview({
        testNumber: testResult.testNumber,
        questionsBySubject: bySubject,
        answers,
        score: testResult.score
      });
      setReviewSubjectIndex(0);
      setViewingTestList(false);
    } catch (e) {}
  };

  const handleExitReview = () => {
    setViewingTestReview(null);
    setReviewSubjectIndex(0);
  };

  const handleBackToProgressFromList = () => {
    setViewingTestList(false);
  };

  // ===== PAST TEST REVIEW SCREEN =====
  if (viewingTestReview) {
    const reviewSubject = SUBJECTS[reviewSubjectIndex];
    const reviewQs = viewingTestReview.questionsBySubject[reviewSubject] || [];
    const isFirst = reviewSubjectIndex === 0;
    const isLast = reviewSubjectIndex === SUBJECTS.length - 1;

    return (
      <div className="sun-container">
        <div className="sun-header">
          <button className="sun-back-btn" onClick={handleExitReview}>←</button>
          <div className="sun-header-title">
            {SUBJECT_LABELS_MR[reviewSubject]} · {t.test} {viewingTestReview.testNumber}
          </div>
          <div style={{ width: 36 }} />
        </div>
        <div className="sun-content">
          {reviewQs.map((q, idx) => {
            const userAns = viewingTestReview.answers[q.id];
            const correctAns = q.correctAnswer;
            const isCorrect = userAns === correctAns;
            const isEmpty = !userAns;
            return (
              <div key={q.id} className="sun-review-card">
                <div className="sun-review-qnum">{idx + 1} / {reviewQs.length}</div>
                <div className="sun-review-qtext">{q.question}</div>

                <div className="sun-review-row">
                  <span className="sun-review-label">{t.yourAnswer}:</span>
                  {isEmpty ? (
                    <span className="sun-review-empty">{t.emptyAnswer}</span>
                  ) : (
                    <span className={`sun-review-answer ${isCorrect ? 'correct' : 'wrong'}`}>
                      {userAns}. {q.options?.[userAns] || ''}
                    </span>
                  )}
                </div>

                {!isCorrect && (
                  <div className="sun-review-row">
                    <span className="sun-review-label">{t.correctAnswer}:</span>
                    <span className="sun-review-answer correct">
                      {correctAns}. {q.options?.[correctAns] || ''}
                    </span>
                  </div>
                )}

                {q.explanation && (
                  <div className="sun-review-explanation">
                    <strong>{t.explanation}:</strong> {q.explanation}
                  </div>
                )}
              </div>
            );
          })}

          <div className="sun-nav-row">
            {!isFirst && (
              <button className="sun-btn sun-btn-secondary"
                onClick={() => { setReviewSubjectIndex(i => i - 1); window.scrollTo({ top: 0 }); }}>
                {t.reviewPrevBtn}
              </button>
            )}
            {!isLast && (
              <button className="sun-btn sun-btn-primary"
                onClick={() => { setReviewSubjectIndex(i => i + 1); window.scrollTo({ top: 0 }); }}>
                {t.reviewNextBtn}
              </button>
            )}
            <button className="sun-btn sun-btn-submit" onClick={handleExitReview}>
              {t.reviewDoneBtn}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== PAST TESTS LIST SCREEN =====
  if (viewingTestList) {
    return (
      <div className="progress-container">
        <div className="progress-header">
          <button className="back-btn-progress" onClick={handleBackToProgressFromList}>
            {t.backToProgress}
          </button>
          <div className="progress-title">{t.reviewListTitle}</div>
          <div style={{ width: 60 }} />
        </div>
        <div className="progress-content">
          {testResults.length === 0 ? (
            <div className="progress-loading">{t.testEmpty}</div>
          ) : (
            <div className="past-tests-list">
              {testResults.map(tr => (
                <button key={tr.id} className="past-test-row" onClick={() => handlePickTest(tr)}>
                  <div className="past-test-name">{t.test} {tr.testNumber}</div>
                  <div className="past-test-score">{tr.score || 0} / 60</div>
                  <div className="past-test-arrow">→</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="progress-container">
        <div className="progress-header">
          <div className="progress-title">{t.title}</div>
          <button className="lang-toggle" onClick={handleLanguageToggle}>
            {language === 'en' ? 'मराठी' : 'EN'}
          </button>
        </div>
        <div className="progress-loading">...</div>
      </div>
    );
  }

  return (
    <div className="progress-container">
      <div className="progress-header">
        <div className="progress-title">{t.title}</div>
        <button className="lang-toggle" onClick={handleLanguageToggle}>
          {language === 'en' ? 'मराठी' : 'EN'}
        </button>
      </div>

      <div className="progress-tabs">
        <button
          className={`progress-tab progress-tab-fitness ${activeTab === 'fitness' ? 'active' : ''}`}
          onClick={() => setActiveTab('fitness')}
        >
          {language === 'mr' ? 'व्यायाम' : 'Fitness'}
        </button>
        <button
          className={`progress-tab progress-tab-lekhi ${activeTab === 'lekhi' ? 'active' : ''}`}
          onClick={() => setActiveTab('lekhi')}
        >
          {language === 'mr' ? 'लेखी' : 'Lekhi'}
        </button>
      </div>

      <div className="progress-content">

        {activeTab === 'fitness' && (
          <>
            <div className="hero-message">{heroMessage}</div>

            <div className="chart-block">
              <div className="chart-title">{t.trainingDays}</div>
              <div className="scroll-wrapper" ref={scrollRef1}>
                <div className="training-grid">
                  {weeksData.map((week, wIdx) => {
                    const daysCount = getDaysCompleted(week);
                    const showNumber = daysCount > 0;
                    return (
                      <div key={wIdx} className="training-col scrollable-col">
                        <div className="bar-number">{showNumber ? daysCount : ''}</div>
                        <div className="dots-stack">
                          {week.days.map((day, dIdx) => {
                            let className = 'mini-dot';
                            if (day.isCompleted) className += ' completed';
                            else if (day.isBeforeSignup || day.isFuture) className += ' light';
                            else className += ' missed';
                            if (day.dateStr === todayStr && !day.isCompleted) {
                              className += ' today';
                            }
                            return <div key={dIdx} className={className} />;
                          })}
                        </div>
                        <div className="x-label">{week.userWeekNum}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="x-axis-title">{t.weeks}</div>
            </div>

            <div className="chart-block">
              <div className="chart-title">{t.squats} · {totalSquats.toLocaleString()} {t.total}</div>
              <div className="scroll-wrapper" ref={scrollRef2}>
                <div className="bars-container">
                  {weeksData.map((week, idx) => {
                    const height = hasSquatsData ? (week.squats / maxSquats) * 100 : 0;
                    const isBest = hasSquatsData && idx === bestSquatsWeekIndex && week.squats > 0;
                    const showNumber = week.squats > 0;
                    let barClass = 'bar';
                    if (week.isCurrentWeek) barClass += ' current';
                    else barClass += ' completed';
                    return (
                      <div key={idx} className="bar-wrap scrollable-col">
                        <div className="pin-slot">{isBest && <DropPin />}</div>
                        <div className="bar-number">{showNumber ? week.squats : ''}</div>
                        <div className="bar-track">
                          <div className={barClass} style={{ height: `${height}%` }} />
                        </div>
                        <div className="x-label">{week.userWeekNum}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="x-axis-title">{t.weeks}</div>
            </div>

            <div className="chart-block">
              <div className="chart-title">{t.jumpingJacks} · {totalJJ.toLocaleString()} {t.total}</div>
              <div className="scroll-wrapper" ref={scrollRef3}>
                <div className="bars-container">
                  {weeksData.map((week, idx) => {
                    const height = hasJJData ? (week.jumpingJacks / maxJJ) * 100 : 0;
                    const isBest = hasJJData && idx === bestJJWeekIndex && week.jumpingJacks > 0;
                    const showNumber = week.jumpingJacks > 0;
                    let barClass = 'bar';
                    if (week.isCurrentWeek) barClass += ' current';
                    else barClass += ' completed';
                    return (
                      <div key={idx} className="bar-wrap scrollable-col">
                        <div className="pin-slot">{isBest && <DropPin />}</div>
                        <div className="bar-number">{showNumber ? week.jumpingJacks : ''}</div>
                        <div className="bar-track">
                          <div className={barClass} style={{ height: `${height}%` }} />
                        </div>
                        <div className="x-label">{week.userWeekNum}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="x-axis-title">{t.weeks}</div>
            </div>
          </>
        )}

        {activeTab === 'lekhi' && (
          <>
            {lekhiLoading ? (
              <div className="progress-loading">...</div>
            ) : (
              <>
                <div className="hero-message hero-lekhi">{lekhiHeroMessage}</div>

                <div className="chart-block">
                  <div className="chart-title">{t.lekhiStudyDays}</div>
                  <div className="scroll-wrapper" ref={lekhiScrollRef1}>
                    <div className="training-grid">
                      {lekhiWeeksData.map((week, wIdx) => {
                        const daysCount = week.days.filter(d => d.isCompleted).length;
                        const showNumber = daysCount > 0;
                        return (
                          <div key={wIdx} className="training-col scrollable-col">
                            <div className="bar-number">{showNumber ? daysCount : ''}</div>
                            <div className="dots-stack">
                              {week.days.map((day, dIdx) => {
                                let className = 'mini-dot lekhi-dot';
                                if (day.isCompleted) className += ' completed';
                                else if (day.isBeforeStart || day.isFuture) className += ' light';
                                else className += ' missed';
                                if (day.dateStr === todayStr && !day.isCompleted) {
                                  className += ' today';
                                }
                                return <div key={dIdx} className={className} />;
                              })}
                            </div>
                            <div className="x-label">{week.userWeekNum}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="x-axis-title">{t.weeks}</div>
                </div>

                <div className="chart-block">
                  <div className="chart-title">{t.topicsCovered}</div>
                  <div className="topics-coverage-list">
                    {SUBJECTS.map(s => {
                      const subjectCoverage = topicsCoverage[s] || {};
                      const levels = Object.keys(subjectCoverage).filter(l => subjectCoverage[l] > 0);
                      if (levels.length === 0) {
                        return (
                          <div key={s} className="coverage-row">
                            <div className="coverage-label">
                              <span className="coverage-subject">{subjectLabel(s)}</span>
                              <span className="coverage-level">—</span>
                            </div>
                            <div className="coverage-bar-track">
                              <div className="coverage-bar-fill" style={{ width: '0%' }} />
                            </div>
                            <div className="coverage-count">0 / {TOPICS_PER_SUBJECT}</div>
                          </div>
                        );
                      }
                      return levels.map(l => {
                        const count = subjectCoverage[l];
                        const pct = (count / TOPICS_PER_SUBJECT) * 100;
                        return (
                          <div key={`${s}-${l}`} className="coverage-row">
                            <div className="coverage-label">
                              <span className="coverage-subject">{subjectLabel(s)}</span>
                              <span className="coverage-level">{levelLabel(l)}</span>
                            </div>
                            <div className="coverage-bar-track">
                              <div className="coverage-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="coverage-count">{count} / {TOPICS_PER_SUBJECT}</div>
                          </div>
                        );
                      });
                    })}
                  </div>
                </div>

                <div className="chart-block">
                  <div className="chart-title">{t.sundayTests}</div>
                  {testResults.length === 0 ? (
                    <div className="lekhi-empty-state">{t.testEmpty}</div>
                  ) : (
                    <>
                      <div className="scroll-wrapper" ref={lekhiScrollRef2}>
                        <div className="bars-container">
                          {testResults.map((tr) => {
                            const height = (tr.score / 60) * 100;
                            return (
                              <div key={tr.id} className="bar-wrap scrollable-col">
                                <div className="pin-slot" />
                                <div className="bar-number">{tr.score || 0}</div>
                                <div className="bar-track">
                                  <div className="bar lekhi-bar" style={{ height: `${height}%` }} />
                                </div>
                                <div className="x-label">{tr.testNumber}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="x-axis-title">{t.test}</div>
                    </>
                  )}
                </div>

                {testResults.length > 0 && (
                  <button className="review-past-tests-btn" onClick={handleReviewPastTests}>
                    {t.reviewPastTests}
                  </button>
                )}
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default ProgressPage;